import React, {
  useState,
  useEffect,
  FC,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { connect, useDispatch } from 'react-redux';
import { Scrollbars } from 'react-custom-scrollbars';
import { createEvent, getEvents, editEvent, deleteEvent } from 'actions/events';
import { IEvents, IEventsState } from 'types';
import { IRootState } from 'store';
import _ from 'lodash';
import { timezone } from 'utils/helpers/momentTime';
import moment, { Moment } from 'moment';
import Title from 'components/common/Title';
import MobileService from 'services/mobile';
import TimeLine from './timeLine';
import Table from './table';
import CalendarHeader from './calendarHeader';
import styles from './index.module.scss';
import ContainerWithShadow from '../ContainerWithShadow';

const mapStateToProps = (state: IRootState) => ({
  eventsData: state.events,
  activeScholarId: state.users.userData?.activeScholarId,
});
interface ICalendar {
  searcIndex: (
    inputArray: moment.Moment[][],
    searchValue: string
  ) => number[] | null;
  eventsData: IEventsState;
  setSelectedDate: Dispatch<SetStateAction<Date>>;
  kidName: string;
  activeScholarId: number | undefined;
}
const Calendar: FC<ICalendar> = ({
  eventsData,
  kidName,
  searcIndex,
  setSelectedDate,
  activeScholarId,
}) => {
  const dispatch = useDispatch();
  const [currentMoment] = useState(moment());
  const [startDay, setStartDay] = useState(
    moment().clone().startOf('year').startOf('week')
  );
  const [lastDay, setLastDay] = useState(
    currentMoment.clone().endOf('year').endOf('week')
  );
  const [daysToShow, setDaysToShow] = useState<number>(1);
  const [week, setWeeks] = useState(0);
  const [days, setDays] = useState<Moment[][]>([]);
  const [timeLine, setTimeLine] = useState<string[]>([]);
  const [events, setEvents] = useState<IEvents[] | []>(eventsData.eventsList);
  const [isWeekly, setIsWeekly] = useState(false);

  const scroll = useRef<Scrollbars>(null);

  const getEventsData = () => {
    if (activeScholarId && !_.isEmpty(days)) {
      if (days[week]) {
        // const daysCount = isWeekly ? 6 : 0;
        const startDate = moment(days[week][0])
          .startOf('week')
          .subtract(timezone, 'minutes')
          .format();
        const endDate = moment(startDate).clone().add(7, 'days').format();
        dispatch(getEvents(activeScholarId, startDate, endDate));
      }
      setEvents(eventsData.eventsList);
    }
  };

  useEffect(() => {
    getEventsData();
    const top = ((scroll.current?.getScrollHeight() || 0) * 7) / 24;
    scroll.current?.scrollTop(top);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [week, days, activeScholarId]);

  useEffect(() => {
    setEvents(eventsData.eventsList);
  }, [eventsData.eventsList, week]);

  useEffect(() => {
    const day = startDay.clone().subtract(1, 'day');
    const calendar = [];

    while (day.isBefore(lastDay, 'day')) {
      calendar.push(
        Array(daysToShow)
          .fill(0)
          .map(() => day.add(1, 'day').clone())
      );
    }
    const currentWeek = searcIndex(calendar, moment().format('YYYY-MM-DD'));
    if (currentWeek) {
      setWeeks(currentWeek[0]);
    }
    setDays(calendar);
    // don't need searcIndex dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysToShow, currentMoment, lastDay, startDay]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { destination, draggableId } = result;
    const newDate = destination.droppableId.split('_')[0];
    const newTimeFrom = destination.droppableId.split('_')[1];
    const timeRangeInMinutes = +draggableId.split('_')[1];
    const id = draggableId.split('_')[0];
    const newstate = [...events];

    if (newDate && newTimeFrom) {
      newstate.map((e) => {
        if (+id === e.id) {
          e.date = moment(newDate).format('YYYY-MM-DD');
          e.startTimeStr = moment(newTimeFrom, 'hh:mm A').format('HH:mm');
          e.endTimeStr = moment(newTimeFrom, 'hh:mm A')
            .add(timeRangeInMinutes, 'minutes')
            .format('HH:mm');
        }
        return null;
      });
      setEvents(newstate);
    }
  };

  const handleChangeWeek = (value: number) => {
    setWeeks(value);
  };

  const addEvent = async (
    eventData: IEvents,
    unitId: number,
    onSuccess?: () => void
  ) => {
    if (createEvent) {
      await dispatch(createEvent(eventData, unitId, onSuccess));

      if (eventData.repeat) {
        getEventsData();
      }
    }
  };

  const handleEditEvent = async (
    unitId: number,
    eventId: number,
    data: IEvents,
    onSuccess?: () => void,
    repeatAction?: string
  ) => {
    const updatedEvents: IEvents[] = [...events];
    // TODO: is it okay to check for data.subject?
    if (data.title !== '' && data.subject) {
      updatedEvents.map((e: IEvents) => {
        if (eventId === e.id) {
          e.link = data.link;
          e.isOnline = data.isOnline;
          e.title = data.title;
          e.subject = data.subject;
          e.date = moment(data.to).toISOString();
          // eslint-disable-next-line prefer-destructuring
          e.startTimeStr = data.startTime.split(' ')[1];
          // eslint-disable-next-line prefer-destructuring
          e.endTimeStr = data.endTime.split(' ')[1];
        }
        return null;
      });
    }
    await dispatch(editEvent(unitId, eventId, data, onSuccess, repeatAction));
    getEventsData();
  };

  const handleDeleteEvent = async (
    unitId: number,
    eventId: number,
    repeatAction?: string
  ) => {
    const updatedEvents: IEvents[] = [];
    events.forEach((e: IEvents) => {
      if (e.id !== eventId) {
        updatedEvents.push(e);
      }
    });

    await dispatch(deleteEvent(unitId, eventId, repeatAction));
    getEventsData();
    setEvents(updatedEvents);
  };
  return (
    <ContainerWithShadow
      className={`${styles.calendar} ${
        MobileService.isMobile ? styles.tablet : ''
      }`}
    >
      <Title className={styles.title}>
        {`Build ${kidName} Learning Workflow Map`}
      </Title>
      {!_.isEmpty(days) && (
        <CalendarHeader
          addEvent={addEvent}
          week={week}
          isWeekly={isWeekly}
          setIsWeekly={setIsWeekly}
          days={days}
          handleChangeWeek={handleChangeWeek}
          setSelectedDate={setSelectedDate}
          searcIndex={searcIndex}
          setDaysToShow={setDaysToShow}
          currentMoment={currentMoment}
          date={days[week]}
          lastDay={lastDay}
          startDay={startDay}
          setLastDay={setLastDay}
          setStartDay={setStartDay}
        />
      )}
      <div className={styles.scrollbarWrapper}>
        <div className={styles.daysHeader}>
          {!_.isEmpty(days) &&
            !_.isEmpty(days[week]) &&
            days[week].map((day: Moment) => (
              <span key={day.format('DD-MM-YYYY')}>{`${moment(day).format(
                'dddd, D'
              )}`}</span>
            ))}
        </div>
        <Scrollbars ref={scroll} autoHide>
          <div className={styles.calendarContent}>
            <div className={styles.calendarBlock}>
              <TimeLine
                cellSize="S"
                onChange={setTimeLine}
                timeLine={timeLine}
              />
              <DragDropContext
                onDragEnd={(result: DropResult) => onDragEnd(result)}
              >
                <Table
                  days={days}
                  weekCount={week}
                  daysToShow={daysToShow}
                  addEvent={addEvent}
                  events={events}
                  timeLine={timeLine}
                  editEvent={handleEditEvent}
                  deleteEvent={handleDeleteEvent}
                  activeScholarId={activeScholarId}
                />
              </DragDropContext>
            </div>
          </div>
        </Scrollbars>
      </div>
    </ContainerWithShadow>
  );
};

export default connect(mapStateToProps)(Calendar);
